/**
 * Sea to Summit — registration engine.
 * Handles: attribution capture/persistence, analytics event dispatch,
 * scroll/section/FAQ tracking, sticky mobile CTA, and the multi-participant
 * registration form (participant fieldsets -> /cart/add.js line items).
 *
 * No PII (name/email/phone/DOB/emergency/guardian/medical/address) is ever
 * passed into stsAnalytics.track(). Line-item properties (sent to Shopify
 * order data, not analytics) are the only place PII is stored.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sts_attribution';
  var UTM_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'fbclid', 'gclid', 'msclkid',
    'campaign_id', 'adset_id', 'ad_id', 'placement', 'creative', 'audience',
    'partner', 'referral', 'promo', 'qr', 'landing_page_version'
  ];

  // ---------------------------------------------------------------------
  // Attribution capture (first-touch persisted forever, last-touch updates
  // every visit that carries tracking params). Uses localStorage — no PII.
  // ---------------------------------------------------------------------
  var Attribution = {
    _read: function () {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch (e) {
        return {};
      }
    },
    _write: function (data) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) { /* storage unavailable — degrade silently */ }
    },
    capture: function () {
      var params = new URLSearchParams(window.location.search);
      var current = {};
      var hasAny = false;
      UTM_KEYS.forEach(function (key) {
        if (params.has(key)) {
          current[key] = params.get(key);
          hasAny = true;
        }
      });

      var stored = this._read();
      var now = new Date().toISOString();

      if (!stored.first_touch) {
        stored.first_touch = {
          timestamp: now,
          url: window.location.href.split('?')[0],
          referrer: document.referrer || null,
          params: hasAny ? current : {}
        };
      }

      if (hasAny) {
        stored.last_touch = {
          timestamp: now,
          url: window.location.href.split('?')[0],
          params: current
        };
      } else if (!stored.last_touch) {
        stored.last_touch = {
          timestamp: now,
          url: window.location.href.split('?')[0],
          params: {}
        };
      }

      stored.session_count = (stored.session_count || 0) + (sessionStorage.getItem('sts_session_started') ? 0 : 1);
      sessionStorage.setItem('sts_session_started', '1');

      stored.current_url = window.location.href;

      this._write(stored);
      return stored;
    },
    get: function () {
      return this._read();
    },
    // Flattened key/value map suitable for Shopify cart attributes (strings only)
    toCartAttributes: function () {
      var d = this.get();
      var ft = d.first_touch || {};
      var lt = d.last_touch || {};
      var out = {
        '_event_name': 'Sea to Summit',
        '_event_year': '2027',
        '_first_touch_timestamp': ft.timestamp || '',
        '_first_touch_url': ft.url || '',
        '_first_touch_referrer': ft.referrer || '',
        '_last_touch_timestamp': lt.timestamp || '',
        '_last_touch_url': lt.url || ''
      };
      var ftp = ft.params || {};
      var ltp = lt.params || {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'].forEach(function (k) {
        out['_first_touch_' + k] = ftp[k] || '';
        out['_last_touch_' + k] = ltp[k] || '';
      });
      ['fbclid', 'gclid', 'msclkid', 'campaign_id', 'adset_id', 'ad_id', 'placement',
        'creative', 'audience', 'partner', 'referral', 'promo', 'qr', 'landing_page_version'
      ].forEach(function (k) {
        out['_' + k] = ltp[k] || ftp[k] || '';
      });
      return out;
    }
  };

  // ---------------------------------------------------------------------
  // Analytics dispatch — fires a DOM CustomEvent (for any listener/QA tooling)
  // and forwards to gtag / fbq / dataLayer only if each already exists.
  // Never installs or duplicates a pixel/tag itself.
  // ---------------------------------------------------------------------
  var Analytics = {
    track: function (eventName, params) {
      params = params || {};
      var payload = Object.assign({
        event_name: 'sea_to_summit',
        event_year: '2027',
        page_type: 'event_registration',
        landing_page_version: new URLSearchParams(window.location.search).get('landing_page_version') || 'a'
      }, params);

      try {
        document.dispatchEvent(new CustomEvent('sts:' + eventName, { detail: payload }));
      } catch (e) { /* noop */ }

      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, payload));
      }
      if (typeof window.fbq === 'function' && eventName.indexOf('registration_') === 0) {
        // Custom (non-standard) events only — standard commerce events
        // (ViewContent/AddToCart/InitiateCheckout/Purchase) are left to
        // Shopify's native Meta integration to avoid duplication.
        window.fbq('trackCustom', eventName, payload);
      }
    }
  };

  // ---------------------------------------------------------------------
  // Scroll depth + section visibility (IntersectionObserver, fire-once)
  // ---------------------------------------------------------------------
  function initScrollAndSectionTracking() {
    var firedDepths = {};
    var depths = [25, 50, 75, 90];
    function onScroll() {
      var scrolled = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
      depths.forEach(function (d) {
        if (scrolled >= d && !firedDepths[d]) {
          firedDepths[d] = true;
          Analytics.track('scroll_depth', { depth_percent: d });
        }
      });
    }
    window.addEventListener('scroll', throttle(onScroll, 500), { passive: true });

    var sections = document.querySelectorAll('[data-sts-section]');
    if (!sections.length || !('IntersectionObserver' in window)) return;
    var seen = {};
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var name = entry.target.getAttribute('data-sts-section');
        if (entry.isIntersecting && !seen[name]) {
          seen[name] = true;
          Analytics.track('section_view', { section_name: name });
        }
      });
    }, { threshold: 0.4 });
    sections.forEach(function (el) { observer.observe(el); });
  }

  function throttle(fn, wait) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn.apply(this, arguments);
      }
    };
  }

  // ---------------------------------------------------------------------
  // FAQ analytics — fires once per question on open, category only (no text)
  // ---------------------------------------------------------------------
  function initFaqTracking() {
    document.querySelectorAll('[data-sts-faq]').forEach(function (details) {
      var fired = false;
      details.addEventListener('toggle', function () {
        if (details.open && !fired) {
          fired = true;
          Analytics.track('faq_open', { faq_category: details.getAttribute('data-sts-faq') || 'unknown' });
        }
      });
    });
  }

  // ---------------------------------------------------------------------
  // CTA click tracking — any element with data-analytics-event="cta_click"
  // ---------------------------------------------------------------------
  function initCtaTracking() {
    document.querySelectorAll('[data-analytics-event]').forEach(function (el) {
      el.addEventListener('click', function () {
        Analytics.track(el.getAttribute('data-analytics-event'), {
          cta_location: el.getAttribute('data-analytics-location') || '',
          section_name: el.getAttribute('data-section') || ''
        });
      });
    });
  }

  // ---------------------------------------------------------------------
  // Sticky mobile registration bar — scroll to form, hide when form section visible
  // ---------------------------------------------------------------------
  function initStickyBar() {
    var bar = document.querySelector('[data-sts-sticky-bar]');
    var formSection = document.querySelector('[data-sts-section="registration_section"]');
    if (!bar) return;

    if (formSection && 'IntersectionObserver' in window) {
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('sts-sticky-bar--hidden', entry.isIntersecting);
        });
      }, { threshold: 0.15 });
      obs.observe(formSection);
    }

    var btn = bar.querySelector('[data-sts-sticky-register]');
    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        Analytics.track('sticky_mobile_cta_click', { cta_location: 'sticky_mobile_register' });
        var target = document.getElementById('sts-registration-form');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  // ---------------------------------------------------------------------
  // Registration form controller
  // ---------------------------------------------------------------------
  var DISCOVERY_OPTIONS = [
    'Instagram', 'Facebook', 'Meta advertisement', 'Dursty email', 'Dursty website',
    'Friend or family member', 'Fellow lifeguard or coworker', 'Lifeguard agency',
    'School or college', 'Surf, swim, or athletic club', 'Local business',
    'Poster or flyer', 'QR code', 'Another event', 'Google search',
    'Community organization', 'Other'
  ];
  var SHIRT_SIZES = ['Small', 'Medium', 'Large', 'X-Large'];

  function calcAge(dobStr) {
    if (!dobStr) return null;
    var dob = new Date(dobStr);
    if (isNaN(dob.getTime())) return null;
    var form = document.getElementById('sts-registration-form');
    var configuredDate = form ? form.getAttribute('data-sts-event-date') : null;
    var eventDay = configuredDate ? new Date(configuredDate) : new Date();
    if (isNaN(eventDay.getTime())) eventDay = new Date();
    var age = eventDay.getFullYear() - dob.getFullYear();
    var m = eventDay.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && eventDay.getDate() < dob.getDate())) age--;
    return age;
  }

  function participantFieldsetHTML(index) {
    var n = index + 1;
    var discoveryOptionsHTML = DISCOVERY_OPTIONS.map(function (opt) {
      return '<option value="' + opt + '">' + opt + '</option>';
    }).join('');
    var shirtOptionsHTML = SHIRT_SIZES.map(function (s) {
      return '<option value="' + s + '">' + s + '</option>';
    }).join('');

    return (
      '<fieldset class="sts-participant" data-sts-participant="' + index + '">' +
        '<legend class="sts-participant__legend">Participant ' + n + '</legend>' +

        '<div class="sts-field-row">' +
          '<div class="sts-field"><label>First name *<input type="text" name="p' + index + '_first_name" required autocomplete="given-name"></label></div>' +
          '<div class="sts-field"><label>Last name *<input type="text" name="p' + index + '_last_name" required autocomplete="family-name"></label></div>' +
        '</div>' +

        '<div class="sts-field-row">' +
          '<div class="sts-field"><label>Date of birth *<input type="date" name="p' + index + '_dob" required data-sts-dob="' + index + '"></label></div>' +
          '<div class="sts-field sts-field--readonly"><label>Age on event day<input type="text" name="p' + index + '_age_display" data-sts-age-display="' + index + '" readonly placeholder="—"></label></div>' +
        '</div>' +

        '<div class="sts-field-row">' +
          '<div class="sts-field"><label>Email *<input type="email" name="p' + index + '_email" required autocomplete="email"></label></div>' +
          '<div class="sts-field"><label>Phone *<input type="tel" name="p' + index + '_phone" required autocomplete="tel"></label></div>' +
        '</div>' +

        '<div class="sts-field-row">' +
          '<div class="sts-field"><label>Emergency contact name *<input type="text" name="p' + index + '_ec_name" required></label></div>' +
          '<div class="sts-field"><label>Emergency contact phone *<input type="tel" name="p' + index + '_ec_phone" required></label></div>' +
        '</div>' +

        '<div class="sts-field-row">' +
          '<div class="sts-field"><label>Running club, team, or organization<input type="text" name="p' + index + '_org"></label></div>' +
          '<div class="sts-field"><label>Relevant certifications<input type="text" name="p' + index + '_certifications"></label></div>' +
        '</div>' +

        '<div class="sts-field sts-field--medical"><label>Optional medical information staff should reasonably know<textarea name="p' + index + '_medical" rows="2"></textarea></label></div>' +

        '<div class="sts-guardian-block" data-sts-guardian-block="' + index + '" hidden>' +
          '<p class="sts-guardian-note">This participant is a minor as of the event date — a parent or guardian must be listed.</p>' +
          '<div class="sts-field-row">' +
            '<div class="sts-field"><label>Parent/guardian name *<input type="text" name="p' + index + '_guardian_name" data-sts-guardian-required="' + index + '"></label></div>' +
            '<div class="sts-field"><label>Parent/guardian email *<input type="email" name="p' + index + '_guardian_email" data-sts-guardian-required="' + index + '"></label></div>' +
            '<div class="sts-field"><label>Parent/guardian phone *<input type="tel" name="p' + index + '_guardian_phone" data-sts-guardian-required="' + index + '"></label></div>' +
          '</div>' +
        '</div>' +

        '<div class="sts-field sts-field--shirt">' +
          '<label for="p' + index + '_shirt_size">Event T-Shirt Size *</label>' +
          '<p class="sts-help-text">Please select the event T-shirt size you would like to receive. Register by [GUARANTEED T-SHIRT DEADLINE] to receive your selected size. Registrations received after that date may receive a shirt based on remaining availability.</p>' +
          '<select id="p' + index + '_shirt_size" name="p' + index + '_shirt_size" required data-sts-shirt-select="' + index + '">' +
            '<option value="" disabled selected>Select a size</option>' +
            shirtOptionsHTML +
          '</select>' +
        '</div>' +

        '<div class="sts-field">' +
          '<label for="p' + index + '_discovery_source">How did you hear about Sea to Summit? *</label>' +
          '<select id="p' + index + '_discovery_source" name="p' + index + '_discovery_source" required data-sts-discovery-select="' + index + '">' +
            '<option value="" disabled selected>Select one</option>' +
            discoveryOptionsHTML +
          '</select>' +
        '</div>' +

        '<div class="sts-field sts-discovery-detail" data-sts-discovery-detail-block="' + index + '" hidden>' +
          '<label>Please tell us where you heard about the event.<input type="text" name="p' + index + '_discovery_detail"></label>' +
        '</div>' +

        '<div class="sts-field"><label>Who referred you? (optional)<input type="text" name="p' + index + '_referral_name" placeholder="Person, organization, agency, club, business, team, ambassador..."></label></div>' +
        '<div class="sts-field"><label>Referral organization (optional)<input type="text" name="p' + index + '_referral_org"></label></div>' +

        '<div class="sts-acknowledgments">' +
          '<label class="sts-checkbox"><input type="checkbox" name="p' + index + '_ack_waiver" required> I have read and accept the <a href="[WAIVER LINK]" target="_blank" rel="noopener" data-analytics-event="waiver_link_click">participant waiver and assumption of risk</a>. *</label>' +
          '<label class="sts-checkbox"><input type="checkbox" name="p' + index + '_ack_rules" required> I have read and accept the <a href="[RULEBOOK LINK]" target="_blank" rel="noopener" data-analytics-event="rules_link_click">event rules and sportsmanship policy</a>. *</label>' +
          '<label class="sts-checkbox"><input type="checkbox" name="p' + index + '_ack_refund" required> I have read and accept the <a href="#sts-policies" data-analytics-event="refund_policy_click">refund, transfer, cancellation, and weather policies</a>: [REFUND POLICY] *</label>' +
          '<label class="sts-checkbox"><input type="checkbox" name="p' + index + '_ack_photo" required> I consent to event photography and video coverage. *</label>' +
          '<label class="sts-checkbox"><input type="checkbox" name="p' + index + '_ack_accurate" required> I confirm the information above is accurate. *</label>' +
          '<label class="sts-checkbox sts-checkbox--optional" data-sts-tshirt-ack-wrap hidden><input type="checkbox" name="p' + index + '_ack_tshirt_deadline"> I understand my selected T-shirt size is only guaranteed if I register before the published T-shirt deadline.</label>' +
        '</div>' +
      '</fieldset>'
    );
  }

  function renderParticipants(container, count) {
    var html = '';
    for (var i = 0; i < count; i++) html += participantFieldsetHTML(i);
    container.innerHTML = html;
    wireParticipantBehaviors(container, count);
  }

  function wireParticipantBehaviors(container, count) {
    for (var i = 0; i < count; i++) {
      (function (index) {
        var dobInput = container.querySelector('[data-sts-dob="' + index + '"]');
        var ageDisplay = container.querySelector('[data-sts-age-display="' + index + '"]');
        var guardianBlock = container.querySelector('[data-sts-guardian-block="' + index + '"]');
        var guardianInputs = container.querySelectorAll('[data-sts-guardian-required="' + index + '"]');

        if (dobInput) {
          dobInput.addEventListener('change', function () {
            var age = calcAge(dobInput.value);
            if (ageDisplay) ageDisplay.value = age === null ? '' : age + (age === 1 ? ' year' : ' years');
            var isMinor = age !== null && age < 18;
            if (guardianBlock) guardianBlock.hidden = !isMinor;
            guardianInputs.forEach(function (el) { el.required = isMinor; });
          });
        }

        var discoverySelect = container.querySelector('[data-sts-discovery-select="' + index + '"]');
        var discoveryDetailBlock = container.querySelector('[data-sts-discovery-detail-block="' + index + '"]');
        if (discoverySelect) {
          discoverySelect.addEventListener('change', function () {
            var isOther = discoverySelect.value === 'Other';
            if (discoveryDetailBlock) discoveryDetailBlock.hidden = !isOther;
            Analytics.track('referral_source_select', { referral_category: discoverySelect.value });
          });
        }

        var shirtSelect = container.querySelector('[data-sts-shirt-select="' + index + '"]');
        if (shirtSelect) {
          shirtSelect.addEventListener('change', function () {
            shirtSelect.setCustomValidity('');
            Analytics.track('shirt_size_select', { shirt_size: shirtSelect.value });
          });
          shirtSelect.addEventListener('invalid', function () {
            shirtSelect.setCustomValidity('Please select an event T-shirt size.');
            Analytics.track('registration_validation_error', { validation_error_type: 'shirt_size' });
          });
        }
      })(i);
    }
  }

  function fieldCategoryForInvalidField(el) {
    var name = el.name || '';
    if (/_shirt_size$/.test(name)) return 'shirt_size';
    if (/_discovery_source$/.test(name)) return 'referral_source';
    if (/^p\d+_(first_name|last_name)$/.test(name)) return 'participant_name';
    if (/_(email|phone)$/.test(name) && name.indexOf('guardian') === -1 && name.indexOf('ec_') === -1) return 'participant_contact';
    if (/^p\d+_ec_/.test(name)) return 'emergency_contact';
    if (/guardian/.test(name)) return 'guardian_information';
    if (/^p\d+_ack_(waiver)/.test(name)) return 'waiver';
    if (/^p\d+_ack_/.test(name)) return 'required_policy';
    return 'unknown';
  }

  function initRegistrationForm() {
    var form = document.getElementById('sts-registration-form');
    if (!form) return;

    var countSelect = form.querySelector('[data-sts-participant-count]');
    var participantsContainer = form.querySelector('[data-sts-participants-container]');
    var submitBtn = form.querySelector('[data-sts-submit]');
    var statusEl = form.querySelector('[data-sts-status]');
    var variantId = form.getAttribute('data-sts-variant-id');
    var formStarted = false;

    function trackFormStartOnce() {
      if (!formStarted) {
        formStarted = true;
        Analytics.track('registration_start', {});
      }
    }

    if (countSelect && participantsContainer) {
      renderParticipants(participantsContainer, parseInt(countSelect.value, 10) || 1);
      countSelect.addEventListener('change', function () {
        trackFormStartOnce();
        renderParticipants(participantsContainer, parseInt(countSelect.value, 10) || 1);
        Analytics.track('participant_form_progress', { field_category: 'participant_count' });
      });
    }

    form.addEventListener('focusin', function () {
      trackFormStartOnce();
    }, { once: true });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      Analytics.track('registration_submit_attempt', {});

      if (!form.checkValidity()) {
        var firstInvalid = form.querySelector(':invalid');
        if (firstInvalid) {
          Analytics.track('registration_validation_error', {
            validation_error_type: fieldCategoryForInvalidField(firstInvalid)
          });
          firstInvalid.reportValidity();
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      Analytics.track('participant_form_complete', { participant_count: countSelect ? countSelect.value : '1' });

      var count = parseInt(countSelect.value, 10) || 1;
      var fd = new FormData(form);
      var items = [];

      for (var i = 0; i < count; i++) {
        var g = function (key) { return (fd.get('p' + i + '_' + key) || '').toString().trim(); };
        var shirtSize = g('shirt_size');
        var props = {
          'Participant Name': g('first_name') + ' ' + g('last_name'),
          'Event T-Shirt Size': shirtSize,
          'Participant #': (i + 1) + ' of ' + count,
          '_participant_first_name': g('first_name'),
          '_participant_last_name': g('last_name'),
          '_participant_dob': g('dob'),
          '_participant_age': g('age_display'),
          '_participant_email': g('email'),
          '_participant_phone': g('phone'),
          '_emergency_contact_name': g('ec_name'),
          '_emergency_contact_phone': g('ec_phone'),
          '_agency_club_school': g('org'),
          '_certifications': g('certifications'),
          '_medical_notes': g('medical'),
          '_guardian_name': g('guardian_name'),
          '_guardian_email': g('guardian_email'),
          '_guardian_phone': g('guardian_phone'),
          'Event discovery source': g('discovery_source'),
          'Event discovery detail': g('discovery_detail'),
          'Referral name': g('referral_name'),
          'Referral organization': g('referral_org'),
          '_waiver_accepted': fd.get('p' + i + '_ack_waiver') ? 'Yes' : 'No',
          '_rules_accepted': fd.get('p' + i + '_ack_rules') ? 'Yes' : 'No',
          '_refund_policy_accepted': fd.get('p' + i + '_ack_refund') ? 'Yes' : 'No',
          '_photo_consent': fd.get('p' + i + '_ack_photo') ? 'Yes' : 'No',
          '_tshirt_deadline_ack': fd.get('p' + i + '_ack_tshirt_deadline') ? 'Yes' : 'No'
        };
        items.push({ id: parseInt(variantId, 10), quantity: 1, properties: props });
      }

      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Submitting…'; }
      if (statusEl) statusEl.textContent = '';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart_add_failed');
          return res.json();
        })
        .then(function () {
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: Attribution.toCartAttributes() })
          });
        })
        .then(function () {
          Analytics.track('registration_add_to_cart', {
            product_id: form.getAttribute('data-sts-product-id') || '',
            variant_id: variantId,
            product_price: '30.00',
            currency: 'USD',
            quantity: count
          });
          window.location.href = '/cart';
        })
        .catch(function () {
          if (statusEl) statusEl.textContent = 'Something went wrong adding your registration to the cart. Please try again, or contact [CONTACT EMAIL].';
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Submit'; }
        });
    });
  }

  // ---------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    Attribution.capture();
    Analytics.track('event_page_view', {});
    initScrollAndSectionTracking();
    initFaqTracking();
    initCtaTracking();
    initStickyBar();
    initRegistrationForm();
  });

  window.stsRegistration = { Attribution: Attribution, Analytics: Analytics };
})();
